import React, { useEffect, useCallback, useRef, useState } from "react";
import { Modal } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";
import { useAuth } from "../../../context/Auth";
import Auth0LoadingScreen from "./Auth0LoadingScreen";
import Auth0LoginFailedScreen from "./Auth0LoginFailedScreen";
import Auth0SessionSupersededScreen from "./Auth0SessionSupersededScreen";
import Auth0EmailVerificationPending from "./Auth0EmailVerificationPending";
import { useProducerServerAuth } from "./useProducerServerAuth";
import { getAuth0LogoutOptions } from "./auth0Helpers";
import { getDevSession, applyDevSession } from "./devSession";

const AUTH0_FADE_DURATION = 0.38;

// DEV-only Auth0 bypass. Always null outside `NODE_ENV === "development"`.
// Resolved once at module load so the gate never flips between modes at runtime.
const devSession = getDevSession();

/**
 * Stable component type (must not be declared inside Authentication) so children
 * like Auth0LoadingScreen are not remounted on every parent state update - avoids
 * a one-frame flash of default .auth-page CSS background.
 */
const Auth0GateContent = ({
  children,
  error,
  isLoading,
  isAuthenticated,
  loginWithRedirect,
  logout,
  user,
  theme,
  loginStatus,
  tryMsg,
  supersededSession,
  onReconnect,
}) => {
  const handleLogout = useCallback(() => {
    logout(getAuth0LogoutOptions());
  }, [logout]);

  useEffect(() => {
    if (!error) return;

    Modal.error({
      title: "Oops!",
      content:
        'Não foi possível iniciar a sessão.<br/>Isto pode acontecer porque não foi confirmado/permitido acesso durante o processo de autenticação.<br/><br/><a href="https://salespark.io/redirect/?u=nao-foi-possivel-iniciar-a-sessao" target="_blank" rel="noreferrer">Saber mais acerca deste erro.</a>',
      onOk: handleLogout,
    });
  }, [error, handleLogout]);

  useEffect(() => {
    if (!isAuthenticated && !isLoading && !error) {
      loginWithRedirect();
    }
  }, [isAuthenticated, isLoading, error, loginWithRedirect]);

  let transitionKey = "loading";
  let body = <Auth0LoadingScreen theme={theme} tryMsg={tryMsg} />;

  if (supersededSession) {
    // A newer login elsewhere took the single session. Highest priority: show it even over the app.
    transitionKey = "superseded";
    body = <Auth0SessionSupersededScreen theme={theme} session={supersededSession} onReconnect={onReconnect} />;
  } else if (!loginStatus.running && loginStatus.completed && !loginStatus.success) {
    transitionKey = "failed";
    body = <Auth0LoginFailedScreen theme={theme} onLogout={handleLogout} onRetry={loginWithRedirect} />;
  } else if (isAuthenticated && !loginStatus.running && loginStatus.completed && loginStatus.success && !user?.email_verified) {
    transitionKey = "email-verify";
    body = <Auth0EmailVerificationPending onUseOtherAccount={handleLogout} />;
  } else if (isAuthenticated && !loginStatus.running && loginStatus.completed && loginStatus.success) {
    transitionKey = "app";
    body = children;
  }

  const skipEnterFade = transitionKey === "loading";
  const motionInitial = skipEnterFade ? false : { opacity: 0 };

  return (
    <AnimatePresence initial={false} exitBeforeEnter>
      <motion.div
        key={transitionKey}
        initial={motionInitial}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: AUTH0_FADE_DURATION, ease: [0.4, 0, 0.2, 1] }}
        style={{
          minHeight: transitionKey === "app" ? "100%" : "100vh",
          width: "100%",
        }}
      >
        {body}
      </motion.div>
    </AnimatePresence>
  );
};

const Authentication = ({ children }) => {
  const { logout, error, isLoading, user, isAuthenticated, loginWithRedirect, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const { auth, setAuth } = useAuth();
  const theme = useSelector((state) => state.theme.currentTheme);
  const devSessionApplied = useRef(false);

  const [supersededSession, setSupersededSession] = useState(null);

  // The API client fires sp:session-superseded (once) when a newer login elsewhere took the single
  // session. Flip the gate to the superseded screen, which stops the app making more authed calls.
  useEffect(() => {
    const onSuperseded = (event) => setSupersededSession(event?.detail || {});
    window.addEventListener("sp:session-superseded", onSuperseded);
    return () => window.removeEventListener("sp:session-superseded", onSuperseded);
  }, []);

  // Deliberate reclaim: re-login re-enrols this browser's DPoP key and takes the single session back
  // here. The full redirect also resets the API client's one-shot guard.
  const handleReconnect = useCallback(() => {
    loginWithRedirect();
  }, [loginWithRedirect]);

  const { loginStatus, tryMsg, serverAuth } = useProducerServerAuth({
    getAccessTokenSilently,
    getIdTokenClaims,
    user,
    setAuth,
    loginWithRedirect,
    logout,
  });

  // DEV bypass: replay the stored session instead of the Auth0 round-trip.
  useEffect(() => {
    if (!devSession || devSessionApplied.current) return;
    devSessionApplied.current = true;
    applyDevSession(devSession, setAuth);
  }, [setAuth]);

  useEffect(() => {
    if (devSession) return;
    if (!isAuthenticated || isLoading) return;
    if (loginStatus.running || loginStatus.completed) return;
    serverAuth();
  }, [isAuthenticated, isLoading, loginStatus.running, loginStatus.completed, serverAuth]);

  if (devSession) {
    // The superseded screen takes priority even under the DEV bypass, so a newer login elsewhere is
    // honoured (and the flow stays testable in development).
    if (supersededSession) return <Auth0SessionSupersededScreen theme={theme} session={supersededSession} onReconnect={handleReconnect} />;
    // One render pass before the effect signs the context in: keep the usual loading
    // screen so no route mounts without `auth.data`.
    if (!auth?.isSignedIn) return <Auth0LoadingScreen theme={theme} tryMsg="A iniciar sessão (DEV)" />;
    return <>{children}</>;
  }

  return (
    <Auth0GateContent
      error={error}
      isLoading={isLoading}
      isAuthenticated={isAuthenticated}
      loginWithRedirect={loginWithRedirect}
      logout={logout}
      user={user}
      theme={theme}
      loginStatus={loginStatus}
      tryMsg={tryMsg}
      supersededSession={supersededSession}
      onReconnect={handleReconnect}
    >
      {children}
    </Auth0GateContent>
  );
};

export default Authentication;
